import { useState, useEffect, useRef } from "react";
import { LazyImage } from "../LazyImage";
import { Button, Dialog, DialogBody } from "@material-tailwind/react";
import { TrashIcon, ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import { t } from "i18next";
import { toast } from "react-hot-toast";

// Composant Skeleton pour l'upload d'image
interface SkeletonCardProps {
  className?: string;
}

export const SkeletonCard = ({ className }: SkeletonCardProps) => {
  return (
    <div className={`relative ${className}`}>
      {/* Image skeleton avec animation de pulse */}
      <div className="w-full h-full bg-gray-200 rounded-lg animate-pulse flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
          <p className="text-sm text-gray-500 font-medium">{t("Uploading...")}</p>
        </div>
      </div>

      {/* Overlay avec indicateur de progression */}
      <div className="absolute inset-0 bg-white bg-opacity-80 rounded-lg flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-4 border">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            <div>
              <p className="text-sm font-medium text-gray-700">{t("Uploading image...")}</p>
              <p className="text-xs text-gray-500">{t("Please wait")}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

type MediaItem = EasyTech.Photo | EasyTech.WorkorderPhoto | EasyTech.WorkorderSignature;

interface PhotoCardProps {
  workorderId: string;
  photo: MediaItem;
  onDelete: (photoId: string) => Promise<void>;
  className?: string;
  type: "BEFORE" | "AFTER";
  getDownloadUrl: (photoId: string) => string;
  uploadType: "photos" | "signatures";
  canEdit?: boolean;
}

export const PhotoCard = ({
  workorderId,
  photo,
  onDelete,
  className,
  type,
  getDownloadUrl,
  uploadType,
  canEdit = true
}: PhotoCardProps) => {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isImageError, setIsImageError] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [key] = useState(`image-${photo.id}`); // Clé stable pour le composant
  const imageUrlRef = useRef<string | null>(null);

  // Garder à jour la ref avec la valeur actuelle
  useEffect(() => {
    imageUrlRef.current = imageUrl;
  }, [imageUrl]);

  // Fonction pour gérer le chargement d'une nouvelle image blob
  const handleImageLoad = (url: string) => {
    setImageUrl(url);
  };

  // Nettoyer l'URL blob lors du démontage ou changement de photo
  useEffect(() => {
    return () => {
      const currentUrl = imageUrlRef.current;
      if (currentUrl && currentUrl.startsWith("blob:")) {
        URL.revokeObjectURL(currentUrl);
      }
    };
  }, [photo.id]);

  const handleDelete = async () => {
    try {
      await onDelete(photo.id);
      setIsDeleteDialogOpen(false);
    } catch (error) {
      console.error("Error deleting photo:", error);
      toast.error(t("Failed to delete photo"));
    }
  };

  const handleDownload = async () => {
    try {
      if (!imageUrl) {
        console.error("Image URL not loaded");
        return;
      }
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${uploadType}-${type}-${photo.id}.jpg`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error(`Erreur lors du téléchargement du ${uploadType}:`, error);
      toast.error(t("Failed to download file"));
    }
  };

  const handleFullscreenToggle = () => {
    setIsFullscreen(!isFullscreen);
  };

  if (isImageError) return null;

  const categoryLabel = "categoryLabel" in photo ? photo.categoryLabel : undefined;

  return (
    <>
      <div className="group relative">
        {categoryLabel && (
          <span className="absolute bottom-2 left-2 z-10 max-w-[calc(100%-1rem)] truncate rounded-full bg-black/60 px-2 py-0.5 text-xs text-white pointer-events-none">
            {categoryLabel}
          </span>
        )}
        <LazyImage
          key={key}
          workorderId={workorderId}
          photoId={photo.id}
          alt={`${uploadType === "signatures" ? "Signature" : "Photo"} ${photo.id}`}
          className={`${className} ${
            isFullscreen ? "cursor-zoom-out" : "cursor-zoom-in"
          } ${uploadType === "signatures" ? "max-w-[400px] max-h-[400px]" : ""}`}
          onClick={handleFullscreenToggle}
          onLoad={handleImageLoad}
          onError={() => setIsImageError(true)}
          type={type}
          getDownloadUrl={getDownloadUrl}
        />
        <div className="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button
            onClick={handleDownload}
            className="p-1 bg-white rounded-full shadow hover:bg-gray-100"
            disabled={!canEdit || !imageUrl}
          >
            <ArrowDownTrayIcon className="h-5 w-5 text-gray-700" />
          </Button>
          <Button
            onClick={() => setIsDeleteDialogOpen(true)}
            className="p-1 bg-white rounded-full shadow hover:bg-gray-100"
            disabled={!canEdit}
          >
            <TrashIcon className="h-5 w-5 text-red-500" />
          </Button>
        </div>
      </div>

      <Dialog
        open={isDeleteDialogOpen}
        handler={() => setIsDeleteDialogOpen(false)}
      >
        <DialogBody className="flex flex-col gap-4">
          <p>
            {t(
              `Are you sure you want to delete this ${
                uploadType === "signatures" ? "signature" : "photo"
              }?`
            )}
          </p>
          <div className="flex justify-end gap-2">
            <button
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
              onClick={() => setIsDeleteDialogOpen(false)}
            >
              {t("Cancel")}
            </button>
            <button
              className="px-4 py-2 bg-red-500 text-white hover:bg-red-600 rounded"
              onClick={handleDelete}
            >
              {t("Delete")}
            </button>
          </div>
        </DialogBody>
      </Dialog>

      {/* Dialog plein écran avec l'image directe (pas de LazyImage) */}
      <Dialog
        size="md"
        open={isFullscreen}
        handler={handleFullscreenToggle}
        className="bg-white max-h-[90vh]"
      >
        <DialogBody className="p-4 max-h-[80vh] overflow-auto">
          {categoryLabel && (
            <div className="mb-2 text-sm font-medium text-gray-700">{categoryLabel}</div>
          )}
          {imageUrl && (
            <img
              src={imageUrl}
              alt={`${uploadType === "signatures" ? "Signature" : "Photo"} ${photo.id}`}
              className="w-full h-auto max-h-[70vh] object-contain"
              onClick={handleFullscreenToggle}
            />
          )}
        </DialogBody>
      </Dialog>
    </>
  );
};